import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { FluentProvider } from '@fluentui/react-components';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/app/error-boundary';
import { SessionProvider } from '@/app/session';
import { ErrorState } from '@/components/feedback/ErrorState';
import { ToasterProvider } from '@/components/feedback/Toaster';
import { createQueryClient } from '@/lib/api/query-client';
import { AuthProvider } from '@/lib/auth/auth-provider';
import { RealtimeProvider } from '@/lib/realtime/realtime-provider';
import { TeamsProvider } from '@/lib/teams/teams-context';
import { ThemeProvider } from '@/lib/theme/theme-provider';
import { useTheme } from '@/hooks/useTheme';

/**
 * Provider composition.
 *
 * Order matters:
 *   Teams  -> theme + user context come from the host.
 *   Theme  -> resolves the Fluent theme applied below.
 *   Fluent -> every UI primitive (including toasts and dialogs) needs it.
 *   Query  -> data layer used by session, realtime invalidation and features.
 *   Auth   -> registers the token provider consumed by the API client.
 *   Session-> authorization (local `users` record) on top of authentication.
 *   Realtime, Toaster -> cross cutting UI services.
 *
 * `AppProviders` must be rendered inside a Router: the session redirects to
 * /403 and several providers navigate.
 */

/** Bridges the resolved theme into Fluent. */
function FluentThemeBridge({ children }: { children: ReactNode }): JSX.Element {
  const { fluentTheme } = useTheme();

  return (
    // No className here: Fluent copies it onto every portal mount node, and
    // those nodes are already position:absolute at top:0/left:0 with
    // z-index 1000000. `h-full` turned each one into a full-window layer that
    // swallowed the click after its own mousedown, so no Dropdown opened by
    // mouse. The shell height comes from `#root > .fui-FluentProvider` in
    // styles/index.css, a selector portals never match.
    <FluentProvider theme={fluentTheme} dir="ltr">
      {children}
    </FluentProvider>
  );
}

/** Route level boundary: a render crash never takes the whole shell down. */
function RouteErrorBoundary({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();

  return (
    <ErrorBoundary
      resetKey={location.pathname}
      fallback={(error, reset) => (
        <div className="p-4 md:p-6">
          <ErrorState variant="generic" error={error} onRetry={reset} />
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  // One client per app instance; recreating it would drop the whole cache.
  const [queryClient] = useState(createQueryClient);

  return (
    <TeamsProvider>
      <ThemeProvider>
        <FluentThemeBridge>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <SessionProvider>
                <RealtimeProvider>
                  <ToasterProvider>
                    <RouteErrorBoundary>{children}</RouteErrorBoundary>
                  </ToasterProvider>
                </RealtimeProvider>
              </SessionProvider>
            </AuthProvider>
          </QueryClientProvider>
        </FluentThemeBridge>
      </ThemeProvider>
    </TeamsProvider>
  );
}
